import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import NotificationSettings from "@/components/NotificationSettings";
import {
  EnvelopeSimple, CalendarBlank, FacebookLogo, CheckCircle, Plug, X, Sun, Moon, Monitor,
} from "@phosphor-icons/react";

const PROVIDER_META = {
  gmail:     { Icon: EnvelopeSimple, accent: "#EA4335", hint: "We'll fetch new customer emails and send AI-approved replies from this address." },
  gcalendar: { Icon: CalendarBlank,  accent: "#1A73E8", hint: "The AI checks availability against this calendar and creates events after approval." },
  messenger: { Icon: FacebookLogo,   accent: "#0866FF", hint: "Incoming Messenger threads are unified in your Inbox for AI-drafted replies." },
};

function IntegrationCard({ integ, oauthReady, onConnect, onSandbox, onDisconnect }) {
  const meta = PROVIDER_META[integ.provider] || {};
  const Icon = meta.Icon || Plug;
  const family = integ.family;
  const label =
    family === "google" ? "Sign in with Google" :
    family === "facebook" ? "Connect Facebook Page" : "Connect";
  return (
    <div data-testid={`integ-${integ.provider}`} className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5 flex items-start gap-4">
      <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: `${meta.accent || "#0F52BA"}14` }}>
        <Icon size={22} weight="duotone" style={{ color: meta.accent || "#0F52BA" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <div className="font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{integ.label}</div>
          {integ.connected ? (
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-emerald-600 text-emerald-700 flex items-center gap-1"><CheckCircle size={10} weight="fill" /> Connected</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-500">Not connected</span>
          )}
          {integ.mode === "sandbox" && (
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-amber-500 text-amber-600">Sandbox</span>
          )}
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-500 mb-3 leading-relaxed">{meta.hint}</div>
        {integ.connected && (
          <div className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
            <span className="text-neutral-500 dark:text-neutral-500">Account: </span>{integ.account}
            {integ.display_name && integ.display_name !== integ.account && <span className="text-neutral-500 dark:text-neutral-500"> · {integ.display_name}</span>}
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-500 mt-1">
              Since {new Date(integ.connected_at).toLocaleDateString()}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {integ.connected ? (
            <button
              data-testid={`integ-disconnect-${integ.provider}`}
              onClick={() => onDisconnect(integ)}
              className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 flex items-center gap-1"
            >
              <X size={12} /> Disconnect
            </button>
          ) : (
            <>
              <button
                data-testid={`integ-connect-${integ.provider}`}
                onClick={() => onConnect(integ)}
                className="text-xs px-3 py-1.5 rounded-md text-white flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                style={{ background: meta.accent || "#0F52BA" }}
              >
                <Plug size={12} weight="bold" /> {oauthReady ? label : `${label} (sandbox)`}
              </button>
              {!oauthReady && (
                <button
                  data-testid={`integ-sandbox-${integ.provider}`}
                  onClick={() => onSandbox(integ)}
                  className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                >
                  Enter manually
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectModal({ integ, onClose, onSaved }) {
  const [account, setAccount] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!account.trim()) return;
    setBusy(true);
    try {
      await api.post(`/integrations/${integ.provider}/sandbox-connect`, { account, display_name: displayName });
      toast.success(`${integ.label} connected`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect");
    } finally { setBusy(false); }
  };

  const meta = PROVIDER_META[integ.provider] || {};
  const Icon = meta.Icon || Plug;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${meta.accent || "#0F52BA"}14` }}>
              <Icon size={20} weight="duotone" style={{ color: meta.accent || "#0F52BA" }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Connect</div>
              <div className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{integ.label}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded"><X size={18} /></button>
        </div>

        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 leading-relaxed p-3 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-900 dark:text-amber-200">
          <b>Sandbox mode.</b> Real OAuth for {integ.label} will replace this dialog once API credentials are configured. For now, enter the account you would connect — the app will route AI drafts as if the channel were live.
        </div>

        <div className="mb-3">
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-1">{integ.handle_label}</div>
          <input
            data-testid="integ-connect-account"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            placeholder={integ.provider === "messenger" ? "AuroraHomeServices" : "you@business.com"}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40"
          />
        </div>
        <div className="mb-5">
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-1">Display name (optional)</div>
          <input
            data-testid="integ-connect-displayname"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Aurora Home Services"
            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-700">Cancel</button>
          <button
            data-testid="integ-connect-submit"
            onClick={submit}
            disabled={busy || !account.trim()}
            className="text-sm px-4 py-2 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094] disabled:opacity-60"
          >
            {busy ? "Connecting…" : "Connect"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const [form, setForm] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [oauthReady, setOauthReady] = useState({ google_oauth_ready: false, facebook_oauth_ready: false });
  const [connecting, setConnecting] = useState(null);
  const { theme, setTheme } = useTheme();

  const loadIntegrations = useCallback(async () => {
    const r = await api.get("/integrations");
    setIntegrations(r.data.providers || []);
    setOauthReady({
      google_oauth_ready: !!r.data.google_oauth_ready,
      facebook_oauth_ready: !!r.data.facebook_oauth_ready,
    });
  }, []);

  useEffect(() => {
    // Show a toast when returning from OAuth
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected") === "google") toast.success("Google connected — Gmail + Calendar ready");
    if (p.get("connected") === "facebook") toast.success("Facebook connected — pick a Page below");
    const err = p.get("google_error") || p.get("facebook_error");
    if (err) toast.error(`OAuth failed: ${err}`);
    if (p.get("connected") || err) {
      window.history.replaceState(null, "", "/settings");
    }
    api.get("/settings").then((r) => setForm({
      ...r.data,
      services: (r.data.services || []).map((s, i) => ({ ...s, _key: s._key || `svc-${i}-${Date.now()}` })),
      discount_tiers: (r.data.discount_tiers || []).map((t, i) => ({ ...t, _key: t._key || `tier-${i}-${Date.now()}` })),
    }));
    loadIntegrations();
  }, [loadIntegrations]);

  if (!form) return <div className="p-8 text-sm text-neutral-500 dark:text-neutral-500">Loading…</div>;

  const save = async () => {
    const payload = {
      business_name: form.business_name,
      business_email: form.business_email,
      business_phone: form.business_phone,
      hourly_rate: parseFloat(form.hourly_rate) || 0,
      tone: form.tone,
      services: form.services.map((s) => ({ name: s.name, unit_price: parseFloat(s.unit_price) || 0 })),
      discount_tiers: (form.discount_tiers || [])
        .filter((t) => (t.label || "").trim())
        .map((t) => ({
          label: t.label,
          min_hours: parseFloat(t.min_hours) || 0,
          discount_pct: parseFloat(t.discount_pct) || 0,
        })),
    };
    await api.put("/settings", payload);
    toast.success("Settings saved");
  };

  const disconnect = async (integ) => {
    await api.post(`/integrations/${integ.provider}/disconnect`);
    toast(`${integ.label} disconnected`);
    loadIntegrations();
  };

  const startOauth = async (integ) => {
    const family = integ.family; // "google" or "facebook"
    const oauthAvailable = family === "google" ? oauthReady.google_oauth_ready : oauthReady.facebook_oauth_ready;
    if (!oauthAvailable) {
      // Fall back to sandbox modal
      setConnecting(integ);
      return;
    }
    try {
      const r = await api.get(`/integrations/${family}/authorize`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start OAuth");
    }
  };

  const addService = () => setForm({ ...form, services: [...form.services, { name: "", unit_price: 0, _key: `svc-${Date.now()}-${Math.random()}` }] });
  const removeService = (i) => setForm({ ...form, services: form.services.filter((_, idx) => idx !== i) });

  const addTier = () => setForm({
    ...form,
    discount_tiers: [
      ...(form.discount_tiers || []),
      { label: "", min_hours: 4, discount_pct: 5, _key: `tier-${Date.now()}-${Math.random()}` },
    ],
  });
  const removeTier = (i) => setForm({
    ...form,
    discount_tiers: (form.discount_tiers || []).filter((_, idx) => idx !== i),
  });
  const updateTier = (i, key, value) => {
    const arr = [...(form.discount_tiers || [])];
    arr[i] = { ...arr[i], [key]: value };
    setForm({ ...form, discount_tiers: arr });
  };

  return (
    <div className="p-8 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Settings</div>
      <h1 className="text-4xl font-semibold tracking-tight mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Business profile</h1>

      {/* Business info */}
      <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-6 space-y-4">
        {[
          ["Business name", "business_name"],
          ["Business email", "business_email"],
          ["Business phone", "business_phone"],
          ["Hourly rate ($)", "hourly_rate"],
        ].map(([label, key]) => (
          <div key={key}>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-1">{label}</div>
            <input data-testid={`settings-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40" />
          </div>
        ))}
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-1">AI tone</div>
          <input data-testid="settings-tone" value={form.tone || ""} onChange={(e) => setForm({ ...form, tone: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40" />
        </div>
      </div>

      {/* Appearance */}
      <div className="mt-10">
        <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Appearance</div>
        <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Theme</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-500 mb-4">Choose how AI Office looks. Follows your device by default.</p>
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {[
            { key: "light", label: "Light", Icon: Sun },
            { key: "dark",  label: "Dark",  Icon: Moon },
            { key: "system", label: "System", Icon: Monitor },
          ].map(({ key, label, Icon }) => {
            const isSystemChoice = key === "system";
            const resolvedActive =
              (isSystemChoice && !localStorage.getItem("aiom_theme")) ||
              (!isSystemChoice && theme === key && localStorage.getItem("aiom_theme") === key);
            return (
              <button
                key={key}
                data-testid={`theme-option-${key}`}
                onClick={() => {
                  if (isSystemChoice) {
                    localStorage.removeItem("aiom_theme");
                    const preferDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
                    setTheme(preferDark ? "dark" : "light");
                  } else {
                    setTheme(key);
                  }
                  toast.success(`Theme: ${label}`);
                }}
                className={`border rounded-lg p-4 text-left transition-colors ${
                  resolvedActive
                    ? "border-[#0F52BA] ring-2 ring-[#0F52BA]/20 bg-[#0F52BA]/5"
                    : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/60"
                }`}
              >
                <Icon size={20} weight="duotone" className="mb-2 text-[#0F52BA]" />
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-500 mt-0.5">
                  {key === "light" && "Bright & minimal"}
                  {key === "dark" && "Easy on the eyes"}
                  {key === "system" && "Match device"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Notifications (mobile push) */}
      <div className="mt-10">
        <NotificationSettings />
      </div>

      {/* Integrations */}
      <div className="mt-10">
        <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Integrations</div>
        <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Connect your channels</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-500 mb-4">Sign in to Gmail, Google Calendar, and Facebook Messenger so the AI can read enquiries, check availability, and reply on your behalf — always with your approval.</p>
        {(!oauthReady.google_oauth_ready || !oauthReady.facebook_oauth_ready) && (
          <div data-testid="oauth-notice" className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
            <b>App owner action required.</b> Real Google / Facebook sign-in is only enabled when the app is registered with each provider (one-time developer setup).
            {!oauthReady.google_oauth_ready && <div className="mt-1">Google is missing <code>GOOGLE_CLIENT_ID</code> / <code>GOOGLE_CLIENT_SECRET</code> in the server env.</div>}
            {!oauthReady.facebook_oauth_ready && <div className="mt-1">Facebook is missing <code>FB_APP_ID</code> / <code>FB_APP_SECRET</code> in the server env.</div>}
            Until then, use <b>Enter manually</b> to save an account for sandbox routing.
          </div>
        )}
        <div className="space-y-3">
          {integrations.map((it) => (
            <IntegrationCard
              key={it.provider}
              integ={it}
              oauthReady={it.family === "google" ? oauthReady.google_oauth_ready : oauthReady.facebook_oauth_ready}
              onConnect={startOauth}
              onSandbox={setConnecting}
              onDisconnect={disconnect}
            />
          ))}
        </div>
      </div>

      {/* Pricing catalog */}
      <div className="mt-10 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Pricing catalog</div>
            <div className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">AI uses these prices only — never invents</div>
          </div>
          <button data-testid="settings-add-service" onClick={addService} className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1">+ Add</button>
        </div>
        <div className="space-y-2">
          {form.services.map((s, i) => (
            <div key={s._key || i} className="grid grid-cols-12 gap-2 items-center">
              <input data-testid={`settings-service-name-${i}`} value={s.name} onChange={(e) => { const arr = [...form.services]; arr[i] = { ...arr[i], name: e.target.value }; setForm({ ...form, services: arr }); }} placeholder="Service name" className="col-span-8 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm" />
              <input data-testid={`settings-service-price-${i}`} value={s.unit_price} onChange={(e) => { const arr = [...form.services]; arr[i] = { ...arr[i], unit_price: e.target.value }; setForm({ ...form, services: arr }); }} placeholder="Price" className="col-span-3 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm" />
              <button onClick={() => removeService(i)} className="col-span-1 p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-500">×</button>
            </div>
          ))}
        </div>
      </div>

      {/* Discount tiers */}
      <div className="mt-6 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-6">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Duration discounts</div>
            <div className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Automatic discount when a booking exceeds your set duration thresholds</div>
          </div>
          <button data-testid="settings-add-tier" onClick={addTier} className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1">+ Add tier</button>
        </div>
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-500 mt-4 mb-1">
          <div className="col-span-6">Label</div>
          <div className="col-span-3">Min hours</div>
          <div className="col-span-2">Discount %</div>
          <div className="col-span-1"></div>
        </div>
        <div className="space-y-2">
          {(form.discount_tiers || []).length === 0 && (
            <div className="text-sm text-neutral-500 dark:text-neutral-500 py-3">No tiers yet. Add one to start offering duration-based discounts on quotes.</div>
          )}
          {(form.discount_tiers || []).map((t, i) => (
            <div key={t._key || i} className="grid grid-cols-12 gap-2 items-center">
              <input data-testid={`settings-tier-label-${i}`} value={t.label} onChange={(e) => updateTier(i, "label", e.target.value)} placeholder="e.g. Full-day" className="col-span-6 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm bg-transparent" />
              <input data-testid={`settings-tier-hours-${i}`} value={t.min_hours} onChange={(e) => updateTier(i, "min_hours", e.target.value)} placeholder="8" type="number" min="0" step="0.5" className="col-span-3 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm bg-transparent" />
              <input data-testid={`settings-tier-pct-${i}`} value={t.discount_pct} onChange={(e) => updateTier(i, "discount_pct", e.target.value)} placeholder="10" type="number" min="0" max="100" step="0.5" className="col-span-2 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm bg-transparent" />
              <button data-testid={`settings-tier-remove-${i}`} onClick={() => removeTier(i)} className="col-span-1 p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-500">×</button>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-3 leading-relaxed">
          The highest-matching tier is applied automatically when the quote&rsquo;s duration ≥ min hours. Set on each quote in the Quotes tab.
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button data-testid="settings-save" onClick={save} className="text-sm px-6 py-2 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094]">Save changes</button>
      </div>

      {connecting && (
        <ConnectModal
          integ={connecting}
          onClose={() => setConnecting(null)}
          onSaved={loadIntegrations}
        />
      )}
    </div>
  );
}
